/*
  Read the hidden platformOS forms emitted by the auth pages and the
  layout-level sign-out form. Each form is rendered server-side by
  `{%- include_form 'modules/portal/<name>' -%}` and wrapped in a
  hidden `<div id="auth-form-<name>">` mount. We capture each form's
  action URL + authenticity_token + form_id at module load time so
  React still has the values after #app is cleared on mount.

  Submission goes through fetch() rather than a native form POST. The
  platformOS endpoints behind these forms (/api/sessions and
  /api/customizations) only respond with JSON: 422 with field errors
  on validation failure, 2xx with `{"errors":{}, ...}` and a Location
  header on success. A native form POST would land the user on a raw
  JSON page; AJAX lets us honour the Location and surface errors
  inline.
*/

export interface AuthFormHandle {
  action: string
  authenticityToken: string
  formId: string
  /** True for forms declared with `method: 'delete'`. PlatformOS emits
   *  a hidden `_method` input — we forward it untouched. */
  methodOverride: string | null
}

function readForm(domId: string): AuthFormHandle | null {
  if (typeof document === 'undefined') return null
  const root = document.getElementById(domId)
  if (!root) return null
  const form = root.querySelector('form') as HTMLFormElement | null
  if (!form) return null
  const tokenInput = form.querySelector('input[name="authenticity_token"]') as HTMLInputElement | null
  const formIdInput = form.querySelector('input[name="form_id"]') as HTMLInputElement | null
  const methodInput = form.querySelector('input[name="_method"]') as HTMLInputElement | null
  if (!tokenInput || !formIdInput) return null
  return {
    action: form.action,
    authenticityToken: tokenInput.value,
    formId: formIdInput.value,
    methodOverride: methodInput?.value ?? null,
  }
}

export const FORMS = {
  signIn: readForm('auth-form-sign-in'),
  forgotPassword: readForm('auth-form-forgot-password'),
  passwordReset: readForm('auth-form-password-reset'),
  signOut: readForm('auth-form-sign-out'),
} as const

if (typeof window !== 'undefined') {
  ;(window as unknown as { __MIG_AUTH_FORMS__: typeof FORMS }).__MIG_AUTH_FORMS__ = FORMS
}

export type AuthFormName = keyof typeof FORMS

export class AuthSubmissionError extends Error {
  status: number
  fieldErrors: Record<string, string[]>
  body: unknown
  constructor(message: string, status: number, fieldErrors: Record<string, string[]>, body: unknown) {
    super(message)
    this.name = 'AuthSubmissionError'
    this.status = status
    this.fieldErrors = fieldErrors
    this.body = body
  }
}

/**
 * Submit a captured platformOS form via fetch. The endpoints respond
 * with JSON and a Location header on success, or 422 with `errors`
 * on failure. Returns the redirect URL on success (caller decides
 * whether to follow). Throws AuthSubmissionError on validation
 * failure with field-level messages.
 */
export async function submitAuthForm(
  formName: AuthFormName,
  fields: Record<string, string>,
): Promise<{ redirectTo: string | null; body: unknown }> {
  const handle = FORMS[formName]
  if (!handle) throw new AuthSubmissionError('Form is not available.', 0, {}, null)

  const body = new URLSearchParams()
  body.set('utf8', '✓')
  body.set('authenticity_token', handle.authenticityToken)
  body.set('form_id', handle.formId)
  if (handle.methodOverride) body.set('_method', handle.methodOverride)
  for (const [name, value] of Object.entries(fields)) body.set(name, value)

  const res = await fetch(handle.action, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      Accept: 'application/json',
    },
    body: body.toString(),
  })

  let parsed: unknown = null
  const text = await res.text()
  if (text) {
    try {
      parsed = JSON.parse(text)
    } catch {
      parsed = text
    }
  }

  if (res.status === 422) {
    const errors =
      parsed && typeof parsed === 'object' && 'errors' in parsed
        ? ((parsed as { errors: Record<string, string[]> }).errors ?? {})
        : {}
    const firstMessage = Object.values(errors).flat().filter(Boolean)[0] || 'Submission failed.'
    throw new AuthSubmissionError(firstMessage, 422, errors, parsed)
  }

  if (!res.ok) {
    throw new AuthSubmissionError(
      `Request failed (${res.status}).`,
      res.status,
      {},
      parsed,
    )
  }

  const redirectTo = res.headers.get('location')
  return { redirectTo, body: parsed }
}
